import { CALL_FUNDS_DISTRIBUTION_API } from '../../middleware/authHeaders'
import {
  ACCEPT_JSON_HEADERS,
  FUNDS_DISTRIBUTION_API_ROOT,
  FUNDS_DISTRIBUTION_PAYMENT_PROCESSING_API_ROOT,
} from '../../config/api'
import { successNotification, errorNotification } from '../application/notifications'

// TYPES & ACTIONS
export const RESET_ACCOUNT_REDUCER = 'RESET_ACCOUNT'

export const resetAccount = () => ({ type: RESET_ACCOUNT_REDUCER })

export const GET_ACCOUNT_REQUEST = 'GET_ACCOUNT_REQUEST'
export const GET_ACCOUNT_SUCCESS = 'GET_ACCOUNT_SUCCESS'
export const GET_ACCOUNT_FAILURE = 'GET_ACCOUNT_FAILURE'

export const getAccount = accountId => dispatch => {
  dispatch({
    [CALL_FUNDS_DISTRIBUTION_API]: {
      method: 'GET',
      endpoint: `${FUNDS_DISTRIBUTION_API_ROOT}/account/${accountId}`,
      types: [GET_ACCOUNT_REQUEST, GET_ACCOUNT_SUCCESS, GET_ACCOUNT_FAILURE],
    },
  }).then(response => {
    if (response.error) {
      dispatch(
        errorNotification({
          body: `Error fetching account`,
          dismissInSeconds: 10,
        })
      )
    }
  })
}

export const GET_ACCOUNT_PENDING_PAYMENTS_REQUEST = 'GET_ACCOUNT_PENDING_PAYMENTS_REQUEST'
export const GET_ACCOUNT_PENDING_PAYMENTS_SUCCESS = 'GET_ACCOUNT_PENDING_PAYMENTS_SUCCESS'
export const GET_ACCOUNT_PENDING_PAYMENTS_FAILURE = 'GET_ACCOUNT_PENDING_PAYMENTS_FAILURE'

export const getPendingPayments =
  (accountId, page = 1) =>
  dispatch => {
    dispatch({
      [CALL_FUNDS_DISTRIBUTION_API]: {
        method: 'GET',
        endpoint: `${FUNDS_DISTRIBUTION_API_ROOT}/account/${accountId}/pending?page=${page}`,
        types: [
          GET_ACCOUNT_PENDING_PAYMENTS_REQUEST,
          GET_ACCOUNT_PENDING_PAYMENTS_SUCCESS,
          GET_ACCOUNT_PENDING_PAYMENTS_FAILURE,
        ],
      },
    }).then(response => {
      if (response.error) {
        dispatch(
          errorNotification({
            body: `Error fetching pending payments`,
            dismissInSeconds: 10,
          })
        )
      }
    })
  }

export const GET_ACCOUNT_PROCESSING_PAYMENTS_REQUEST = 'GET_ACCOUNT_PROCESSING_PAYMENTS_REQUEST'
export const GET_ACCOUNT_PROCESSING_PAYMENTS_SUCCESS = 'GET_ACCOUNT_PROCESSING_PAYMENTS_SUCCESS'
export const GET_ACCOUNT_PROCESSING_PAYMENTS_FAILURE = 'GET_ACCOUNT_PROCESSING_PAYMENTS_FAILURE'

export const getProcessingPayments =
  (accountId, page = 1) =>
  dispatch => {
    dispatch({
      [CALL_FUNDS_DISTRIBUTION_API]: {
        method: 'GET',
        endpoint: `${FUNDS_DISTRIBUTION_API_ROOT}/account/${accountId}/processing?page=${page}`,
        types: [
          GET_ACCOUNT_PROCESSING_PAYMENTS_REQUEST,
          GET_ACCOUNT_PROCESSING_PAYMENTS_SUCCESS,
          GET_ACCOUNT_PROCESSING_PAYMENTS_FAILURE,
        ],
      },
    }).then(response => {
      if (response.error) {
        dispatch(
          errorNotification({
            body: `Error fetching processing payments`,
            dismissInSeconds: 10,
          })
        )
      }
    })
  }

export const GET_ACCOUNT_PAYMENTS_REQUEST = 'GET_ACCOUNT_PAYMENTS_REQUEST'
export const GET_ACCOUNT_PAYMENTS_SUCCESS = 'GET_ACCOUNT_PAYMENTS_SUCCESS'
export const GET_ACCOUNT_PAYMENTS_FAILURE = 'GET_ACCOUNT_PAYMENTS_FAILURE'

export const getPayments =
  (accountId, status, createdAt, paymentProcessor, page = 1) =>
  dispatch => {
    dispatch({
      [CALL_FUNDS_DISTRIBUTION_API]: {
        method: 'GET',
        endpoint: `${FUNDS_DISTRIBUTION_API_ROOT}/account/${accountId}/payments?status=${status}&paymentProcessor=${paymentProcessor}&createdAt=${createdAt}&page=${page}`,
        types: [
          GET_ACCOUNT_PAYMENTS_REQUEST,
          GET_ACCOUNT_PAYMENTS_SUCCESS,
          GET_ACCOUNT_PAYMENTS_FAILURE,
        ],
      },
    }).then(response => {
      if (response.error) {
        dispatch(
          errorNotification({
            body: `Error fetching account payments`,
            dismissInSeconds: 10,
          })
        )
      }
    })
  }

export const DOWNLOAD_PAYMENTS_REQUEST = 'DOWNLOAD_PAYMENTS_REQUEST'
export const DOWNLOAD_PAYMENTS_SUCCESS = 'DOWNLOAD_PAYMENTS_SUCCESS'
export const DOWNLOAD_PAYMENTS_FAILURE = 'DOWNLOAD_PAYMENTS_FAILURE'

export const downloadPaymentsFile =
  (accountId, accountName, status, paymentProcessor, createdAt) => dispatch => {
    const query = new URLSearchParams({
      status,
      paymentProcessor,
      createdAt,
    }).toString()

    return dispatch({
      [CALL_FUNDS_DISTRIBUTION_API]: {
        method: 'GET',
        endpoint: `${FUNDS_DISTRIBUTION_API_ROOT}/account/${accountId}/getAllPayments?${query}`,
        types: [
          DOWNLOAD_PAYMENTS_REQUEST,
          {
            type: DOWNLOAD_PAYMENTS_SUCCESS,
            payload: async (action, state, res) => {
              const json = await res.json()

              if (!Array.isArray(json) || json.length === 0) {
                throw new Error('No payment data returned.')
              }

              // Sanitize filename
              const safeAccountName = accountName
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '')

              const headers = Object.keys(json[0])
              const csv = [
                headers.join(','),
                ...json.map(row =>
                  headers.map(key => `"${String(row[key] ?? '').replace(/"/g, '""')}"`).join(',')
                ),
              ].join('\n')

              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
              const url = window.URL.createObjectURL(blob)

              const link = document.createElement('a')
              link.href = url
              link.setAttribute('download', `${safeAccountName}-${paymentProcessor}-${status}.csv`)
              document.body.appendChild(link)
              link.click()
              link.remove()
              window.URL.revokeObjectURL(url)

              return null
            },
          },
          DOWNLOAD_PAYMENTS_FAILURE,
        ],
      },
    }).catch(error => {
      dispatch(
        errorNotification({
          body: `Error downloading payments file: ${error.message}`,
          dismissInSeconds: 10,
        })
      )
    })
  }

export const GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_REQUEST =
  'GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_REQUEST'
export const GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_SUCCESS =
  'GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_SUCCESS'
export const GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_FAILURE =
  'GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_FAILURE'

export const getPaymentsHeldForReview =
  (accountId, page = 1) =>
  dispatch => {
    dispatch({
      [CALL_FUNDS_DISTRIBUTION_API]: {
        method: 'GET',
        endpoint: `${FUNDS_DISTRIBUTION_API_ROOT}/account/${accountId}/held-for-review?page=${page}`,
        types: [
          GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_REQUEST,
          GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_SUCCESS,
          GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_FAILURE,
        ],
      },
    }).then(response => {
      if (response.error) {
        dispatch(
          errorNotification({
            body: `Error fetching held payments`,
            dismissInSeconds: 10,
          })
        )
      }
    })
  }

export const GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_REQUEST =
  'GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_REQUEST'
export const GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_SUCCESS =
  'GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_SUCCESS'
export const GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_FAILURE =
  'GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_FAILURE'

export const getPaymentImportPreview = accountId => dispatch => {
  dispatch({
    [CALL_FUNDS_DISTRIBUTION_API]: {
      method: 'GET',
      endpoint: `${FUNDS_DISTRIBUTION_PAYMENT_PROCESSING_API_ROOT}/processing/${accountId}/preview-payments`,
      types: [
        GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_REQUEST,
        GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_SUCCESS,
        GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_FAILURE,
      ],
    },
  }).then(response => {
    if (response.error) {
      dispatch(
        errorNotification({
          body: `Error fetching payments import preview`,
          dismissInSeconds: 10,
        })
      )
    }
  })
}

export const CREATE_ACCOUNT_REQUEST = 'CREATE_ACCOUNT_REQUEST'
export const CREATE_ACCOUNT_SUCCESS = 'CREATE_ACCOUNT_SUCCESS'
export const CREATE_ACCOUNT_FAILURE = 'CREATE_ACCOUNT_FAILURE'

export const createAccount = body => dispatch => {
  dispatch({
    [CALL_FUNDS_DISTRIBUTION_API]: {
      method: 'POST',
      endpoint: `${FUNDS_DISTRIBUTION_API_ROOT}/account`,
      types: [CREATE_ACCOUNT_REQUEST, CREATE_ACCOUNT_SUCCESS, CREATE_ACCOUNT_FAILURE],
      headers: {
        ...ACCEPT_JSON_HEADERS,
      },
      body: JSON.stringify(body),
    },
  }).then(response => {
    if (response.error) {
      dispatch(
        errorNotification({
          body: `Error creating account`,
          dismissInSeconds: 10,
        })
      )
    } else {
      dispatch(
        successNotification({
          body: `Successfully created account`,
          dismissInSeconds: 5,
        })
      )
    }
  })
}

export const UPDATE_ACCOUNT_REQUEST = 'UPDATE_ACCOUNT_REQUEST'
export const UPDATE_ACCOUNT_SUCCESS = 'UPDATE_ACCOUNT_SUCCESS'
export const UPDATE_ACCOUNT_FAILURE = 'UPDATE_ACCOUNT_FAILURE'

export const updateAccount = body => dispatch => {
  dispatch({
    [CALL_FUNDS_DISTRIBUTION_API]: {
      method: 'PUT',
      endpoint: `${FUNDS_DISTRIBUTION_API_ROOT}/account/${body.accountId}`,
      types: [UPDATE_ACCOUNT_REQUEST, UPDATE_ACCOUNT_SUCCESS, UPDATE_ACCOUNT_FAILURE],
      headers: {
        ...ACCEPT_JSON_HEADERS,
      },
      body: JSON.stringify(body),
    },
  }).then(response => {
    if (!response.error) {
      dispatch(
        successNotification({
          body: `Successfully updated account`,
          dismissInSeconds: 3,
        })
      )
    }
  })
}

// SELECTORS
export const selectAccount = state => state.fundsDistribution.account
export const selectAccountPendingPayments = state => selectAccount(state).pendingPayments
export const selectAccountProcessingPayments = state => selectAccount(state).processingPayments
export const selectAccountPayments = state => selectAccount(state).accountPayments
export const selectAccountPaymentsHeldForReview = state =>
  selectAccount(state).paymentsHeldForReview
export const selectAccountPaymentsImportPreview = state => selectAccount(state).paymentImportPreview

// STATE
const INITIAL_STATE = {
  pendingPayments: {
    items: [],
    currentPage: 0,
    pageSize: 0,
    pageCount: 0,
    rowCount: 0,
    firstRowOnPage: 0,
    lastRowOnPage: 0,
  },
  processingPayments: {
    items: [],
    currentPage: 0,
    pageSize: 0,
    pageCount: 0,
    rowCount: 0,
    firstRowOnPage: 0,
    lastRowOnPage: 0,
  },
  failedPayments: {
    items: [],
    currentPage: 0,
    pageSize: 0,
    pageCount: 0,
    rowCount: 0,
    firstRowOnPage: 0,
    lastRowOnPage: 0,
  },
  accountPayments: {
    items: [],
    currentPage: 0,
    pageSize: 0,
    pageCount: 0,
    rowCount: 0,
    firstRowOnPage: 0,
    lastRowOnPage: 0,
  },
  paymentsHeldForReview: {
    items: [],
    currentPage: 1,
    pageSize: 10,
    pageCount: 1,
    rowCount: 1,
    firstRowOnPage: 1,
    lastRowOnPage: 1,
  },
  paymentImportPreview: [],
}

// REDUCERS
const account = (state = INITIAL_STATE, { type, payload } = {}) =>
  ({
    [RESET_ACCOUNT_REDUCER]: () => ({ ...INITIAL_STATE }),
    [GET_ACCOUNT_SUCCESS]: (currentState, newPayload) => ({ ...currentState, ...newPayload }),
    [GET_ACCOUNT_PENDING_PAYMENTS_SUCCESS]: (currentState, newPayload) => ({
      ...currentState,
      pendingPayments: { ...newPayload },
    }),
    [GET_ACCOUNT_PROCESSING_PAYMENTS_SUCCESS]: (currentState, newPayload) => ({
      ...currentState,
      processingPayments: { ...newPayload },
    }),
    [GET_ACCOUNT_PAYMENTS_SUCCESS]: (currentState, newPayload) => ({
      ...currentState,
      accountPayments: {
        items: newPayload.items,
        currentPage: newPayload.page,
        pageSize: newPayload.pageSize,
        pageCount: newPayload.totalPages,
        rowCount: newPayload.totalItems,
        firstRowOnPage: (newPayload.page - 1) * newPayload.pageSize + 1,
        lastRowOnPage: Math.min(newPayload.page * newPayload.pageSize, newPayload.totalItems),
        totalItems: newPayload.totalItems,
        totalAmount: newPayload.totalAmount ?? 0,
      },
    }),
    [GET_ACCOUNT_PAYMENTS_HELD_FOR_REVIEW_SUCCESS]: (currentState, newPayload) => ({
      ...currentState,
      paymentsHeldForReview: { ...newPayload },
    }),
    [GET_ACCOUNT_PAYMENTS_IMPORT_PREVIEW_SUCCESS]: (currentState, newPayload) => ({
      ...currentState,
      paymentImportPreview: newPayload,
    }),
    [CREATE_ACCOUNT_SUCCESS]: (currentState, newPayload) => ({ ...currentState, ...newPayload }),
    [UPDATE_ACCOUNT_SUCCESS]: (currentState, newPayload) => ({
      ...currentState,
      hasMultiplePaymentMethods: newPayload.hasMultiplePaymentMethods ?? false,
      isArchived: newPayload.isArchived ?? false,
      usBankAchEnableAccountValidationService:
        newPayload.usBankAchEnableAccountValidationService ?? false,
      usBankCheckEnableEmail: newPayload.usBankCheckEnableEmail ?? false,
      usBankAchEnableEmail: newPayload.usBankEnableEmail ?? false,
      ...newPayload,
    }),
  }[type]?.(state, payload) || state)

export default account
